import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPut } from '../../services/api';

function formatEuro(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `€${n.toFixed(2)}`;
}

function formatUpdatedAt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export default function AdminPricingTab({ token, isActive, handleAuthError, setError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [rowError, setRowError] = useState('');

  const loadPricing = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiGet('/api/v1/admin/pricing', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setFetchError('Could not load pricing configuration.');
        return;
      }
      const json = await res.json();
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch {
      setFetchError('Could not load pricing configuration.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    if (isActive) {
      loadPricing();
    }
  }, [isActive, loadPricing]);

  const startEdit = (row) => {
    setEditingKey(row.key);
    setEditValue(String(row.value));
    setRowError('');
    setSavedKey(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
    setRowError('');
  };

  const saveEdit = async (key) => {
    const value = Number(editValue);
    if (!editValue || Number.isNaN(value) || value <= 0) {
      setRowError('Enter a price greater than 0.');
      return;
    }

    setSavingKey(key);
    setRowError('');
    setError('');
    try {
      const res = await apiPut(`/api/v1/admin/pricing/${encodeURIComponent(key)}`, { value }, token);
      if (handleAuthError(res)) return;
      if (res.status === 422) {
        setRowError('Invalid price. Use a value between 0.01 and 99999.99.');
        return;
      }
      if (!res.ok) {
        setRowError('Could not save price.');
        return;
      }
      const json = await res.json();
      const updated = json.data;
      setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...updated } : row)));
      setEditingKey(null);
      setEditValue('');
      setSavedKey(key);
      window.setTimeout(() => setSavedKey(null), 2000);
    } catch {
      setRowError('Could not save price.');
    } finally {
      setSavingKey(null);
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <>
      {fetchError ? (
        <Alert variant="danger" className="mb-3">
          {fetchError}
        </Alert>
      ) : null}
      {rowError ? (
        <Alert variant="danger" className="mb-3">
          {rowError}
        </Alert>
      ) : null}

      <Table responsive className="rwm-admin__table align-middle" data-testid="rwm-admin-pricing-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Current Price</th>
            <th>Last Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isEditing = editingKey === row.key;
            return (
              <tr key={row.key} data-testid={`rwm-pricing-row-${row.key}`}>
                <td>{row.label}</td>
                <td>
                  {isEditing ? (
                    <Form.Control
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      style={{ maxWidth: '8rem' }}
                      data-testid={`rwm-pricing-input-${row.key}`}
                    />
                  ) : (
                    formatEuro(row.value)
                  )}
                </td>
                <td>
                  <div>{formatUpdatedAt(row.updated_at)}</div>
                  {row.updated_by_name ? (
                    <div className="small text-muted">{row.updated_by_name}</div>
                  ) : null}
                  {savedKey === row.key ? (
                    <div className="small" style={{ color: 'var(--rwm-salary)' }}>
                      Saved
                    </div>
                  ) : null}
                </td>
                <td>
                  {isEditing ? (
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        className="text-white"
                        disabled={savingKey === row.key}
                        onClick={() => saveEdit(row.key)}
                        data-testid={`rwm-pricing-save-${row.key}`}
                      >
                        {savingKey === row.key ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        disabled={savingKey === row.key}
                        onClick={cancelEdit}
                        data-testid={`rwm-pricing-cancel-${row.key}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline-info"
                      size="sm"
                      onClick={() => startEdit(row)}
                      data-testid={`rwm-pricing-edit-${row.key}`}
                    >
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}
